import { Search } from '@mui/icons-material'
import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { BreadcrumbItem, Breadcrumbs } from 'components/Breadcrumbs'
import { usePromoter } from 'contexts/promoterContext'
import dayjs from 'dayjs'
import { useFetchData } from 'hooks/use-fetch-data'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { apiV1 } from 'services'
import type { OrderItemByEvent } from 'services/api/v1/orderItemService'
import { datagrid } from 'utils'

const columns: GridColDef<OrderItemByEvent>[] = [
  {
    field: 'id',
    headerName: '#',
    width: 80,
  },
  {
    field: 'sectorName',
    headerName: 'Setor',
    width: 160,
  },
  {
    field: 'lotName',
    headerName: 'Lote',
    width: 200,
  },
  {
    field: 'eventParticipant',
    headerName: 'Participante',
    width: 180,
  },
  {
    field: 'order',
    headerName: 'Pedido',
    width: 120,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    renderCell: (params) =>
      params.value === 'active'
        ? 'Ativo'
        : `Utilizado ${dayjs(params.row.date).format('DD/MM/YYYY HH:mm:ss')}`,
  },
]

export function ParticipantsList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [ticketStatus, setTicketStatus] = useState([1, 2])
  const [selectedSectors, setSelectedSectors] = useState<number[]>([])

  const { eventId } = useParams()
  const { selectedEvent } = usePromoter()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Eventos', to: '/organizador/eventos' },
    {
      title: selectedEvent?.titulo ?? '...',
      to: `/organizador/eventos/${eventId}`,
    },
    { title: 'Lista de participantes' },
  ]

  const queryTickets = useFetchData(
    async () =>
      await apiV1.orderItemService.getOrderItemsByEvent(String(eventId)),
    {
      enabled: !!eventId,
      staleTime: 0,
    },
  )

  useEffect(() => {
    if (queryTickets.data?.items) {
      const sectors = queryTickets.data.items
        .map((x) => x.sectorId)
        .filter((value, index, self) => self.indexOf(value) === index)
      setSelectedSectors(sectors)
    }
  }, [queryTickets.data?.items])

  if (queryTickets.error) {
    return (
      <Grid container spacing={2} sx={{ p: 2 }} component={Paper}>
        <Grid item xs={12}>
          <Breadcrumbs items={breadcrumbs} />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="error">
            Ocorreu um erro ao carregar os participantes. Tente novamente mais
            tarde.
          </Alert>
        </Grid>
      </Grid>
    )
  }

  const filteredData =
    queryTickets.data?.items.filter((x) => {
      const isActive = ticketStatus.includes(1) && x.status === 'active'
      const isUsed = ticketStatus.includes(2) && x.status === 'used'
      const isSearchMatch =
        x.eventParticipant.toLowerCase().includes(searchQuery.toLowerCase()) ||
        x.lotName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        x.sectorName.toLowerCase().includes(searchQuery.toLowerCase())
      const isSectorMatch = selectedSectors.includes(x.sectorId)

      return (isActive || isUsed) && isSearchMatch && isSectorMatch
    }) ?? []

  return (
    <Grid container spacing={2} sx={{ p: 2 }} component={Paper}>
      <Grid item xs={12}>
        <Breadcrumbs items={breadcrumbs} />
      </Grid>

      <Grid item xs={12} sm={8}>
        <TextField
          label="Pesquisar"
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
          size="small"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={ticketStatus.includes(1)}
                onChange={() =>
                  setTicketStatus((prevState) =>
                    prevState.includes(1)
                      ? prevState.filter((x) => x !== 1)
                      : [...prevState, 1],
                  )
                }
              />
            }
            label="Ativo"
          />
          <FormControlLabel
            control={<Checkbox checked={ticketStatus.includes(2)} />}
            onChange={() =>
              setTicketStatus((prevState) =>
                prevState.includes(2)
                  ? prevState.filter((x) => x !== 2)
                  : [...prevState, 2],
              )
            }
            label="Utilizado"
          />
        </FormGroup>
      </Grid>

      <Grid item xs={12}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Setor</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Validados</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queryTickets.data?.info.map((sector) => (
              <TableRow key={sector.id}>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedSectors.includes(sector.id)}
                        size="small"
                      />
                    }
                    onChange={() =>
                      setSelectedSectors((prevState) =>
                        prevState.includes(sector.id)
                          ? prevState.filter((x) => x !== sector.id)
                          : [...prevState, sector.id],
                      )
                    }
                    label={sector.name}
                  />
                </TableCell>
                <TableCell>{sector.sold}</TableCell>
                <TableCell>{sector.validated}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell>Total</TableCell>
              <TableCell>
                {queryTickets.data?.info.reduce(
                  (acc, sector) => acc + Number(sector.sold),
                  0,
                )}
              </TableCell>
              <TableCell>
                {queryTickets.data?.info.reduce(
                  (acc, sector) => acc + Number(sector.validated),
                  0,
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Grid>

      <Grid item xs={12} display="flex">
        <DataGrid
          loading={queryTickets.isLoading}
          columns={columns}
          rows={filteredData}
          localeText={datagrid.localizedTextsMap}
          density="compact"
        />
      </Grid>
    </Grid>
  )
}
